/**
 * Same-origin URL rewriter.
 *
 * When the app is loaded from the custom production domain
 * (ckkcriseofthedukes.online), the frontend bundle is baked with a
 * REACT_APP_BACKEND_URL that points to a different subdomain
 * (e.g. https://chess-cosmetics-shop.emergent.host). Cross-site requests with
 * SameSite=None cookies are blocked by strict browser privacy settings
 * (private mode, Brave Shields, Safari ITP, Firefox Strict, many extensions),
 * resulting in "Network error" on login even though the server responds 200.
 *
 * Cloudflare already routes /api/* on the custom domain to the backend, so
 * same-origin requests work. This module patches fetch() and WebSocket at the
 * global level to rewrite any URL pointing at the old backend host to the
 * current origin when we detect we're on the custom domain.
 *
 * On preview (*.preview.emergentagent.com) or dev, no rewrite happens.
 */

const CUSTOM_DOMAINS = ['ckkcriseofthedukes.online', 'www.ckkcriseofthedukes.online'];

function shouldRewrite() {
  if (typeof window === 'undefined') return false;
  return CUSTOM_DOMAINS.includes(window.location.hostname);
}

function rewriteUrl(url) {
  if (!url || typeof url !== 'string') return url;
  // Only rewrite absolute URLs pointing at emergent.host (the baked backend)
  if (!/^https?:\/\/[^/]*\.emergent\.host(\/|$)/i.test(url)) return url;
  try {
    const u = new URL(url);
    const sameOriginUrl = `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    return sameOriginUrl;
  } catch {
    return url;
  }
}

function rewriteWsUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (!/^wss?:\/\/[^/]*\.emergent\.host(\/|$)/i.test(url)) return url;
  try {
    const u = new URL(url);
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${scheme}//${window.location.host}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

export function installSameOriginRewriter() {
  if (!shouldRewrite()) return;

  // Patch fetch
  const originalFetch = window.fetch;
  if (originalFetch && !originalFetch.__sameOriginPatched) {
    const patched = function (input, init) {
      let rewrittenInput = input;
      if (typeof input === 'string') {
        rewrittenInput = rewriteUrl(input);
      } else if (input instanceof URL) {
        rewrittenInput = rewriteUrl(input.href);
      } else if (input && typeof input === 'object' && 'url' in input) {
        const newUrl = rewriteUrl(input.url);
        if (newUrl !== input.url) {
          rewrittenInput = new Request(newUrl, input);
        }
      }
      return originalFetch.call(this, rewrittenInput, init);
    };
    patched.__sameOriginPatched = true;
    window.fetch = patched;
  }

  // Patch XMLHttpRequest (some code paths / libraries still use it, e.g. axios fallback)
  const OriginalXHR = window.XMLHttpRequest;
  if (OriginalXHR && !OriginalXHR.prototype.__sameOriginPatched) {
    const originalOpen = OriginalXHR.prototype.open;
    OriginalXHR.prototype.open = function (method, url, ...rest) {
      return originalOpen.call(this, method, rewriteUrl(url), ...rest);
    };
    OriginalXHR.prototype.__sameOriginPatched = true;
  }

  // Patch WebSocket
  const OriginalWS = window.WebSocket;
  if (OriginalWS && !OriginalWS.__sameOriginPatched) {
    const PatchedWS = function (url, protocols) {
      const rewritten = rewriteWsUrl(url);
      return protocols !== undefined
        ? new OriginalWS(rewritten, protocols)
        : new OriginalWS(rewritten);
    };
    PatchedWS.prototype = OriginalWS.prototype;
    PatchedWS.CONNECTING = OriginalWS.CONNECTING;
    PatchedWS.OPEN = OriginalWS.OPEN;
    PatchedWS.CLOSING = OriginalWS.CLOSING;
    PatchedWS.CLOSED = OriginalWS.CLOSED;
    PatchedWS.__sameOriginPatched = true;
    window.WebSocket = PatchedWS;
  }
}

export default installSameOriginRewriter;
